import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useLanguage } from "@/context/LanguageContext";
import { listingPostLabels } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/ui/DatePicker";
import {
  Wrench,
  X,
  Calendar as CalendarIcon2,
  Gauge,
  FileText,
} from "lucide-react";
import { format } from "date-fns";
import { ServiceDetail } from "@/types/vehicleTest";
import { nativeNestedSheetDialogProps } from "@/lib/native/nativeNestedSheetProps";
import { cn } from "@/lib/utils";

interface ServiceDetailsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (serviceDetail: ServiceDetail) => void;
  initialData?: ServiceDetail | null;
}

interface ServiceDetailsViewDialogProps {
  isOpen: boolean;
  onClose: () => void;
  serviceDetail: ServiceDetail | null;
}

export const ServiceDetailsDialog: React.FC<ServiceDetailsDialogProps> = ({
  isOpen,
  onClose,
  onSave,
  initialData = null,
}) => {
  const { language } = useLanguage();
  const sd = (listingPostLabels(language).serviceDetails ?? {}) as Record<
    string,
    string | string[]
  >;
  const [date, setDate] = useState<Date | undefined>(
    initialData?.date || undefined,
  );
  const [mileage, setMileage] = useState(
    initialData?.mileage?.toString() || "",
  );
  const [serviceType, setServiceType] = useState(
    initialData?.serviceType || "",
  );
  const [notes, setNotes] = useState(initialData?.notes || "");
  const maxServiceDate = useMemo(() => new Date(), []);

  // Update form when initialData changes
  React.useEffect(() => {
    if (initialData) {
      setDate(initialData.date);
      setMileage(initialData.mileage?.toString() || "");
      setServiceType(initialData.serviceType || "");
      setNotes(initialData.notes || "");
    } else {
      setDate(undefined);
      setMileage("");
      setServiceType("");
      setNotes("");
    }
  }, [initialData]);

  const handleSave = () => {
    if (!date || !mileage.trim() || !serviceType.trim()) {
      return;
    }

    const serviceDetail: ServiceDetail = {
      id: Date.now().toString(),
      date,
      mileage: parseInt(mileage.trim()) || 0,
      serviceType: serviceType.trim(),
      notes: notes.trim(),
    };

    onSave(serviceDetail);
    handleClose();
  };

  const handleClose = () => {
    setDate(undefined);
    setMileage("");
    setServiceType("");
    setNotes("");
    onClose();
  };

  const isValid = date && mileage.trim() && serviceType.trim();
  const nestedSheetProps = nativeNestedSheetDialogProps(isOpen);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        {...nestedSheetProps}
        className={cn(nestedSheetProps.className, "sm:max-w-[500px]")}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wrench className="h-5 w-5" />
            {initialData
              ? (sd.editTitle ?? "Redigera servicedetaljer")
              : (sd.addTitle ?? "Lägg till servicedetaljer")}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="text-sm text-primary/60 mb-4">
            {sd.description ??
              "Detta är en detalj från en specifik service som utförts på fordonet."}
          </div>

          <div className="space-y-2">
            <Label>{sd.date ?? "Datum"}</Label>
            <DatePicker
              value={date}
              onChange={setDate}
              maxDate={maxServiceDate}
              placeholder={sd.selectDate ?? "Välj datum"}
            />
          </div>

          <div className="space-y-2">
            <Label>{sd.mileage ?? "Mätställare (mil)"}</Label>
            <Input
              type="number"
              placeholder={sd.mileagePlaceholder ?? "T.ex. 125000"}
              value={mileage}
              onChange={(e) => setMileage(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>{sd.serviceType ?? "Typ av service"}</Label>
            <Input
              placeholder={
                sd.serviceTypePlaceholder ??
                "T.ex. oljebyte, bromsar, däckbyte.."
              }
              value={serviceType}
              onChange={(e) => setServiceType(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>{sd.notes ?? "Anteckningar"}</Label>
            <Textarea
              placeholder={sd.notesPlaceholder ?? "Ytterligare information"}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={handleClose}>
              {sd.cancel ?? "Avbryt"}
            </Button>
            <Button onClick={handleSave} disabled={!isValid}>
              {sd.save ?? "Spara"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const ServiceDetailsViewDialog: React.FC<
  ServiceDetailsViewDialogProps
> = ({ isOpen, onClose, serviceDetail }) => {
  if (!serviceDetail) return null;

  const nestedSheetProps = nativeNestedSheetDialogProps(isOpen);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        {...nestedSheetProps}
        className={cn(nestedSheetProps.className, "sm:max-w-[500px]")}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wrench className="h-5 w-5" />
            Servicedetaljer
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-sm font-medium text-primary">
              Typ av service
            </Label>
            <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-md border">
              <div className="flex items-center gap-2">
                <Wrench className="h-4 w-4 text-blue-500" />
                <span className="font-medium">{serviceDetail.serviceType}</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium text-primary">Datum</Label>
              <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-md border">
                <div className="flex items-center gap-2">
                  <CalendarIcon2 className="h-4 w-4 text-blue-500" />
                  <span>{format(serviceDetail.date, "yyyy-MM-dd")}</span>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium text-primary">
                Mätarställning
              </Label>
              <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-md border">
                <div className="flex items-center gap-2">
                  <Gauge className="h-4 w-4 text-blue-500" />
                  <span>{serviceDetail.mileage} mil</span>
                </div>
              </div>
            </div>
          </div>

          {serviceDetail.notes && (
            <div className="space-y-2">
              <Label className="text-sm font-medium text-primary">
                Anteckningar
              </Label>
              <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-md border">
                <div className="flex items-start gap-2">
                  <FileText className="h-4 w-4 text-blue-500 mt-0.5" />
                  <span className="whitespace-pre-wrap">
                    {serviceDetail.notes}
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end pt-4">
            <Button variant="outline" onClick={onClose}>
              Stäng
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
